import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { langPrefix } from "../lib/i18nRoutes";

const SITE_URL = "https://alpha-translations.com";
const DEFAULT_IMAGE = `${SITE_URL}/fulllogo-1024x331.jpg`;
const SITE_NAME = "Alpha Translations";

// URL absoluta de uma página (basePath sem prefixo) num idioma.
const urlFor = (lang, basePath) => {
  const prefix = langPrefix(lang); // "" | "/es" | "/en"
  if (basePath === "/") return `${SITE_URL}${prefix || "/"}`;
  return `${SITE_URL}${prefix}${basePath}`;
};

const OG_LOCALE = { pt: "pt_BR", es: "es_ES", en: "en_US" };
const HTML_LANG = { pt: "pt-BR", es: "es", en: "en" };

/**
 * Metadados de SEO por página (title, description, canonical, Open Graph e
 * Twitter Card). Aplique apenas em páginas PÚBLICAS/indexáveis — as áreas
 * autenticadas não precisam e já estão bloqueadas no robots.txt.
 *
 * @param {string} title       Título da aba/SERP (sem o sufixo da marca).
 * @param {string} description Meta description (~150-160 caracteres).
 * @param {string} path        Caminho da rota (ex.: "/login") para o canonical.
 * @param {string} [image]     Imagem Open Graph absoluta (padrão: logo).
 * @param {boolean} [noindex]  Se true, marca a página como não indexável.
 * @param {string} [type]      og:type (padrão "website").
 */
const HREF_LANG = { pt: "pt-BR", es: "es", en: "en" };

const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  noindex = false,
  type = "website",
  // Idiomas em que ESTA página existe (para os hreflang). Padrão: os três.
  langs = ["pt", "es", "en"],
}) => {
  const { i18n } = useTranslation();
  const cur = ["pt", "es", "en"].includes(i18n.language) ? i18n.language : "pt";
  // Se a página não existe no idioma atual, o canonical cai no PT.
  const lang = langs.includes(cur) ? cur : "pt";

  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  // `path` é o caminho BASE (versão PT, sem prefixo). O canonical e os hreflang
  // são derivados por idioma a partir dele.
  const canonical = urlFor(lang, path);

  return (
    <Helmet htmlAttributes={{ lang: HTML_LANG[lang] }}>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      {/* Versões por idioma (hreflang) — só os idiomas em que a página existe. */}
      {langs.map((l) => (
        <link key={l} rel="alternate" hrefLang={HREF_LANG[l]} href={urlFor(l, path)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={urlFor("pt", path)} />

      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      <meta property="og:image" content={image} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default Seo;
